import { useMemo, useState } from "react";
import type { GenieAttachment } from "@/types/voice/genie-attachment";

/** Attachments shown within this window of the newest one belong to the same share (a link + its picture). */
const SAME_SHARE_WINDOW_MS = 3000;

interface MessageWithAttachment {
  timestamp: Date;
  attachment?: GenieAttachment;
}

/**
 * The Genie's most recent share (showRelatedMedia) for the card under the voice
 * avatar. It stays up until the visitor dismisses it or the Genie shares
 * something newer; a fresh conversation (empty message list) clears it.
 */
export const useLatestAttachments = (messages: MessageWithAttachment[]) => {
  const [dismissedShareAt, setDismissedShareAt] = useState<number | null>(null);

  const latestShare = useMemo(() => {
    const shared = messages.filter((message) => message.attachment);
    const newest = shared[shared.length - 1];
    if (!newest) return null;
    const sharedAt = newest.timestamp.getTime();
    return {
      sharedAt,
      attachments: shared
        .filter((message) => sharedAt - message.timestamp.getTime() <= SAME_SHARE_WINDOW_MS)
        .map((message) => message.attachment as GenieAttachment),
    };
  }, [messages]);

  const visible = latestShare && latestShare.sharedAt !== dismissedShareAt ? latestShare.attachments : [];

  return {
    attachments: visible,
    dismiss: () => latestShare && setDismissedShareAt(latestShare.sharedAt),
  };
};
